// src/components/discovery/ProblemCardGrid.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import { Button } from "components/ui/button";
import {
  AlertTriangle,
  Users,
  FileText,
  TrendingUp,
  CheckCircle2,
  Target,
  Loader2,
  ExternalLink,
  Route,
  Link2,
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";
import { Pager, usePaged } from "components/ui/compact";
import EvidenceDrawer from "../evidence/EvidenceDrawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import ProblemCardWindow from "./ProblemCardWindow";
import JiraEpicLinkDialog from "./JiraEpicLinkDialog";
import { problemCardsService } from "../../services/database";
import { scoreEvidenceForProblem, VALIDATION_THRESHOLD } from "../../lib/validationScore";
import { useGuide } from "contexts/GuideContext";
import { EXAMPLE_QUOTE } from "lib/sampleWorkspace";
import SampleBadge from "components/guide/SampleBadge";

// --- robust entity update helper ---
async function robustUpdateProblemStatus(problemId, patch = { status: "validated" }) {
  try {
    console.log('Updating problem status:', problemId, 'with:', patch);
    return await problemCardsService.update(problemId, patch);
  } catch (error) {
    console.error('Error updating problem status:', error);
    throw error;
  }
}

export default function ProblemCardGrid({
  focusProblemId,
  problems = [],
  evidence = [],
  isLoading,
  onAddToRoadmap,
  roadmapItems = [],
  onProblemUpdate,  // parent refresh hook
  onOpenProblem,    // optional custom opener; if omitted we use ProblemCardWindow
}) {
  const [busyId, setBusyId] = useState(null);        // for Validate button
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeProblem, setActiveProblem] = useState(null);
  const [statusOverride, setStatusOverride] = useState({}); // optimistic status map (String keys)
  const [gateId, setGateId] = useState(null);

  // Problem Card Window state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorProblem, setEditorProblem] = useState(null);

  useEffect(() => {
    const focused = problems.find(p => p.id === focusProblemId);
    if (focused) { setEditorProblem(focused); setEditorOpen(true); }
  }, [focusProblemId, problems]);

  // Jira Epic Link state
  const [epicLinkOpen, setEpicLinkOpen] = useState(false);
  const [epicLinkProblem, setEpicLinkProblem] = useState(null);

  const openEvidence = (problem) => {
    setActiveProblem(problem);
    setDrawerOpen(true);
  };

  const openProblemEditor = (p) => {
    if (onOpenProblem) {
      onOpenProblem(p); // allow parent override
      return;
    }
    setEditorProblem(p);
    setEditorOpen(true);
  };

  const afterEvidenceChanged = async () => {
    await onProblemUpdate?.();
  };

  const getConfidenceLevel = (d = 0, f = 0, v = 0) => {
    const avg = (Number(d) + Number(f) + Number(v)) / 3;
    if (avg >= 80) return { level: "High", bg: "var(--success-soft)", color: "var(--success)", avg: Math.round(avg) };
    if (avg >= 50) return { level: "Medium", bg: "var(--warning-soft)", color: "var(--warning)", avg: Math.round(avg) };
    return { level: "Low", bg: "var(--danger-soft)", color: "var(--danger-fg)", avg: Math.round(avg) };
  };

  const evidenceByProblem = useMemo(() => {
    const map = new Map();
    for (const e of evidence) {
      const pid = e?.problem_card_id ?? e?.problem_id ?? e?.problemId ?? e?.problem;
      if (pid == null) continue;
      const key = String(pid);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [evidence]);

  const getEvidenceFor = (id) => evidenceByProblem.get(String(id)) || [];

  // Three rows of cards per page; a new filter result starts again at page 1.
  const paged = usePaged(problems, 9, problems.map((p) => p.id).join());
  const guide = useGuide();

  // Loading
  if (isLoading) {
    return (
      <div className="grid cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton style={{ height: 24, width: "75%", marginBottom: 8 }} />
              <Skeleton style={{ height: 16, width: "100%" }} />
            </CardHeader>
            <CardContent>
              <Skeleton style={{ height: 80, width: "100%", borderRadius: 8, marginBottom: 12 }} />
              <Skeleton style={{ height: 12, width: "50%" }} />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Empty
  if (!problems || problems.length === 0) {
    return (
      <Card>
        <div className="empty">
          <Target size={32} />
          <strong>No problem cards yet</strong>
          <span>Create problem cards to validate customer needs and build evidence-based solutions.</span>
          <Button type="button" variant="primary" style={{ marginTop: 8 }} onClick={() => openEvidence({ id: "new" })} data-guide="problem-add">
            <AlertTriangle size={15} />
            Create your first problem card
          </Button>
          {guide && <Button type="button" variant="secondary" style={{ marginTop: 8 }} onClick={() => guide.openComposer(EXAMPLE_QUOTE)}>Start from an example quote</Button>}
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="grid cols-3">
        {paged.items.map((p, idx) => {
          const override = statusOverride[String(p.id)];
          const effStatus = override ?? p.status;
          const isAlreadyValidated = String(effStatus) === "validated";
          const canMoveToRoadmap = ["validated", "in_roadmap"].includes(effStatus) && !roadmapItems.some(item => (item.problem_card_ids || []).includes(p.id));

          const ev = getEvidenceFor(p.id);
          // Handle JSONB confidence_score or legacy individual fields
          const confidence = p.confidence_score || {};
          const desirability = Number(confidence.desirability || p.desirability_score) || 0;
          const feasibility = Number(confidence.feasibility || p.feasibility_score) || 0;
          const viability = Number(confidence.viability || p.viability_score) || 0;
          const conf = getConfidenceLevel(desirability, feasibility, viability);
          const horizon = p.time_horizon || p.horizon || "";
          const okr = p.okr_ref || p.okr || "";

          const evScore = scoreEvidenceForProblem(evidence, p.id);
          const scoreOk = Number(evScore.score) >= Number(VALIDATION_THRESHOLD);

          const lastSeen =
            evScore.lastEvidenceDays == null
              ? "No date"
              : evScore.lastEvidenceDays === 0
              ? "today"
              : `${evScore.lastEvidenceDays}d ago`;

          const suggestionTitle =
            evScore.suggestions?.length
              ? `Suggestions: ${evScore.suggestions.join(", ")}`
              : "Keep collecting core evidence";

          const validating = busyId === p.id;
          const moving = false;

          return (
            <Card key={p.id} className="interactive" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <Badge variant={STATUS_TONE[effStatus] || "default"} className="badge-dot" style={{ textTransform: "capitalize" }}>
                  {String(effStatus || "new").replace(/_/g, " ")}
                </Badge>
                {p.severity && <Badge variant={SEVERITY_TONE[p.severity] || "default"} style={{ textTransform: "capitalize" }}>{p.severity}</Badge>}
                <SampleBadge id={p.id}/>
                {horizon && <Badge>{horizon}</Badge>}
                <Badge
                  variant={evScore.score >= 80 ? "success" : evScore.score >= 60 ? "warning" : "default"}
                  style={{ marginLeft: "auto" }}
                  title={`${suggestionTitle} • Last evidence: ${lastSeen}`}
                >
                  Evidence {evScore.score}
                </Badge>
              </div>

              <div style={{ minWidth: 0 }}>
                <button
                  type="button"
                  onClick={() => openProblemEditor(p)}
                  title="Open problem card"
                  className="card-link"
                >
                  {p.title}
                </button>
                {(p.description || p.problem) && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 13,
                      color: "var(--fg-muted)",
                      lineHeight: 1.45,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                    title={p.description || p.problem}
                  >
                    {p.description || p.problem}
                  </p>
                )}
              </div>

              {((p.target_segment || p.customer_segment) || okr) && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--fg-muted)" }}>
                  {(p.target_segment || p.customer_segment) && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Users size={13} /> {p.target_segment || p.customer_segment}</span>
                  )}
                  {okr && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Target size={13} /> {okr}</span>}
                </div>
              )}

              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, marginBottom: 8 }}>
                  <span style={{ color: "var(--fg-muted)", fontWeight: 500 }}>Confidence</span>
                  <span className="tabular" style={{ color: conf.color, fontWeight: 600 }} title={`Avg: ${conf.avg}%`}>
                    {conf.level} · {conf.avg}%
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
                  <RowMeter label="Desirability" value={desirability} />
                  <RowMeter label="Feasibility" value={feasibility} />
                  <RowMeter label="Viability" value={viability} />
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--fg-muted)" }}>
                <FileText size={13} />
                <span className="tabular">{ev.length} evidence {ev.length === 1 ? "item" : "items"}</span>
                <span aria-hidden="true">·</span>
                <span title={`Last evidence: ${lastSeen}`}>{lastSeen === "No date" ? "none yet" : `last ${lastSeen}`}</span>
                {p.jira_epic_key && (
                  <a
                    href={p.jira_epic_url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    title={p.jira_epic_title || p.jira_epic_key}
                    style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, color: "var(--accent-fg)", fontWeight: 500, textDecoration: "none" }}
                  >
                    <Link2 size={12} /> {p.jira_epic_key}
                  </a>
                )}
              </div>

              <div style={{ display: "flex", gap: 6, paddingTop: 12, marginTop: "auto", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
                <Button type="button" variant="secondary" size="sm" onClick={() => openEvidence(p)} title="Add or manage evidence" data-guide={idx === 0 ? "evidence-add" : undefined}>
                  <TrendingUp size={14} />
                  Add evidence
                </Button>

                {canMoveToRoadmap ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={moving}
                    onClick={() => onAddToRoadmap?.(p)}
                    title="Review and create a linked initiative"
                    data-guide="initiative-handoff"
                  >
                    {moving ? <Loader2 size={14} style={{ animation: "spin .8s linear infinite" }} /> : <Route size={14} />}
                    Create Initiative
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant={isAlreadyValidated ? "success" : "secondary"}
                    size="sm"
                    disabled={validating || isAlreadyValidated}
                    onClick={async () => {
                      if (isAlreadyValidated) return;
                      if (!scoreOk) { setGateId(p.id); return; }
                      setBusyId(p.id);
                      setStatusOverride((m) => ({ ...m, [String(p.id)]: "validated" })); // optimistic
                      try {
                        await robustUpdateProblemStatus(p.id, {
                          status: "validated",
                          validated_date: new Date().toISOString(),
                        });
                        await onProblemUpdate?.();
                      } catch (err) {
                        console.error("[Validate] failed", err);
                        setStatusOverride((m) => {
                          const copy = { ...m };
                          delete copy[String(p.id)];
                          return copy;
                        });
                      } finally {
                        setBusyId(null);
                      }
                    }}
                    title={
                      isAlreadyValidated
                        ? "Already validated"
                        : !scoreOk
                        ? `Needs score ≥ ${VALIDATION_THRESHOLD}. Click to see what's missing.`
                        : "Mark as validated"
                    }
                  >
                    {validating ? <Loader2 size={14} style={{ animation: "spin .8s linear infinite" }} /> : <CheckCircle2 size={14} />}
                    {isAlreadyValidated ? "Validated" : validating ? "Saving…" : "Validate"}
                  </Button>
                )}

                <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="btn-icon"
                    aria-label={p.jira_epic_key ? `Change linked Jira epic (${p.jira_epic_key})` : "Link a Jira epic"}
                    title={p.jira_epic_key ? `Linked to ${p.jira_epic_key} — click to change` : "Link a Jira epic"}
                    onClick={() => { setEpicLinkProblem(p); setEpicLinkOpen(true); }}
                  >
                    <Link2 size={14} style={{ color: p.jira_epic_key ? "var(--accent)" : undefined }} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="btn-icon"
                    aria-label="Open problem card"
                    title="Open problem card"
                    onClick={() => openProblemEditor(p)}
                  >
                    <ExternalLink size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <div className="v2-pager-row"><Pager paged={paged} label="Problem pages" /></div>

      {/* Evidence Drawer */}
      <EvidenceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        problem={activeProblem}
        evidence={evidence}
        onChanged={afterEvidenceChanged}
      />

      {/* Problem Card Window */}
      <ProblemCardWindow
        open={editorOpen}
        problem={editorProblem}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          setEditorOpen(false);
          await onProblemUpdate?.();
        }}
      />

      {/* Jira Epic Link Dialog */}
      <JiraEpicLinkDialog
        open={epicLinkOpen}
        onClose={() => { setEpicLinkOpen(false); setEpicLinkProblem(null); }}
        problem={epicLinkProblem}
        onLink={async (epicFields) => {
          if (!epicLinkProblem?.id) return;
          await problemCardsService.update(epicLinkProblem.id, epicFields);
          await onProblemUpdate?.();
        }}
      />

      {/* Score gate dialog */}
      <Dialog open={!!gateId} onOpenChange={(v) => !v && setGateId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Not enough evidence to validate</DialogTitle>
            <DialogDescription>
              This card’s evidence score is below the required threshold ({VALIDATION_THRESHOLD}).<br />
              Collect more core evidence (e.g., interviews, usability tests, or analytics) before validating.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Button type="button" variant="outline" onClick={() => setGateId(null)}>Close</Button>
            <Button
              type="button"
              onClick={() => {
                const target = problems.find((x) => String(x.id) === String(gateId));
                setGateId(null);
                if (target) openEvidence(target);
              }}
              variant="primary"
            >
              Add evidence
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RowMeter({ label, value }) {
  return (
    <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 4, fontSize: 11 }}>
        <span className="text-ellipsis" style={{ color: "var(--fg-muted)" }}>{label}</span>
        <span className="tabular" style={{ fontWeight: 600, color: "var(--fg-secondary)" }}>{value}</span>
      </div>
      <Progress value={value} style={{ height: 4 }} />
    </div>
  );
}

const STATUS_TONE = { discovery: "accent", validated: "success", solution_designed: "accent", in_roadmap: "info", solved: "success" };
const SEVERITY_TONE = { low: "default", medium: "warning", high: "warning", critical: "danger" };
